/**
 * Phase D D.11.4 — Modal import CRC PDF externe via Claude Vision.
 * Endpoint : POST /chantiers/{cid}/crc/import-pdf
 */
import { useState } from 'react';
import axios from 'axios';
import { X, DownloadSimple, CheckCircle, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImportExternalCRCDialog({ chantierId, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/import-pdf`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        },
      );
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <DownloadSimple size={24} /> Importer un CR antérieur
          </h2>
          <button onClick={onClose} data-testid="import-crc-close">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Pour les chantiers en cours débutés hors Kroquis, importez les comptes rendus (CR)
            antérieurs au format PDF. L'IA extrait automatiquement :
          </p>
          <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
            <li>Numéro et date du CR</li>
            <li>Intervenants présents</li>
            <li>Points d'actions avec statuts</li>
            <li>Synthèse de la semaine</li>
          </ul>

          {!result && (
            <>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files[0])}
                className="border rounded p-2 w-full text-sm"
                data-testid="import-crc-file-input"
              />
              {file && <p className="text-sm text-green-700">📄 {file.name}</p>}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-start gap-2">
                  <Warning size={18} /> {error}
                </div>
              )}
            </>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <p className="font-semibold text-green-800 flex items-center gap-2">
                <CheckCircle size={20} /> Import réussi
              </p>
              <p className="text-sm">
                CR n°<strong>{result.numero || '?'}</strong> créé
              </p>
              <p className="text-sm">
                <strong>{result.nb_points_imported}</strong> point(s) d'actions importé(s)
              </p>
              {result.ocr_error && (
                <p className="text-xs text-orange-700">
                  ⚠️ Extraction partielle : {result.ocr_error}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">
            {result ? 'Fermer' : 'Annuler'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleImport}
              disabled={!file || importing}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              data-testid="import-crc-submit"
            >
              {importing ? 'Extraction IA en cours…' : "Lancer l'import"}
            </button>
          )}
          {result && (
            <button
              type="button"
              onClick={() => onSuccess?.(result)}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg"
            >
              Voir le CR importé
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
